import React, { useEffect, useState, useCallback, useRef } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const seen = useRef(null);
  const unread = items.filter((n) => !n.read).length;

  const openReq = (n) => { if (n.request_id) navigate(`/requests?open=${n.request_id}`); else navigate("/notifications"); };

  const load = useCallback(async () => {
    try {
      const data = (await api.get("/notifications")).data || [];
      setItems(data);
      if (seen.current === null) {
        seen.current = new Set(data.map((n) => n.id));
      } else {
        const fresh = data.filter((n) => !n.read && !seen.current.has(n.id));
        fresh.slice(0, 3).forEach((n) => {
          toast(n.message, {
            description: n.request_type ? `${(n.request_type || "").replace("_", " ")} · ${n.status || ""}` : "New notification",
            action: n.request_id ? { label: "Open Request", onClick: () => navigate(`/requests?open=${n.request_id}`) } : undefined,
          });
        });
        data.forEach((n) => seen.current.add(n.id));
      }
    } catch (e) { console.error("Failed to load notifications", e); }
  }, [navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch (e) { console.error("Failed to mark all read", e); }
  };
  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); setItems((p) => p.map((n) => (n.id === id ? { ...n, read: true } : n))); } catch (e) { console.error("Failed to mark notification read", e); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative text-slate-300 hover:text-white transition-colors">
          <Bell className="w-[18px] h-[18px]" />
          {unread > 0 && <span data-testid="notification-count" className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-1">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <span className="text-sm font-semibold text-slate-800">Notifications</span>
          {unread > 0 && <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-slate-500 hover:text-[#0B132B] flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? <div className="px-3 py-8 text-center text-sm text-slate-400">No notifications</div>
            : items.slice(0, 30).map((n) => (
              <div key={n.id} onClick={() => { markOne(n.id); openReq(n); setOpen(false); }} data-testid={`notification-item`} className={`px-3 py-2.5 border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${n.read ? "opacity-60" : "bg-blue-50/40"}`}>
                <div className="text-sm text-slate-700">{n.message}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
